/**
 * Flow node components for the workflow flowchart.
 *
 * All nodes render as pill-shaped elements (rounded-full) with consistent
 * sizing. Visual distinction comes from border style and icon, not shape.
 */

import type { LucideIcon } from "lucide-react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import type { FlowStep, FlowStepStatus, NodePosition } from "./types";

// ---------------------------------------------------------------------------
// Icon resolution
// ---------------------------------------------------------------------------

/** Maps step.icon strings to actual Lucide icon components. */
const ICON_MAP: Record<string, LucideIcon> = {
  robot: Icons.Bot,
  bot: Icons.Bot,
  clock: Icons.Clock,
  play: Icons.Play,
  gmail: Icons.Mail,
  mail: Icons.Mail,
  slack: Icons.MessageSquare,
  github: Icons.Github,
  terminal: Icons.Terminal,
  user: Icons.User,
  "git-branch": Icons.GitBranch,
  webhook: Icons.Zap,
  zap: Icons.Zap,
  file: Icons.FileText,
  cloud: Icons.Cloud,
  "check-circle": Icons.CheckCircle,
  brain: Icons.Brain,
  code: Icons.Code,
  settings: Icons.Settings,
  globe: Icons.Globe,
  search: Icons.Search,
  check: Icons.Check,
  messagesquare: Icons.MessageSquare,
};

function resolveIcon(iconKey: string): LucideIcon {
  return ICON_MAP[iconKey.toLowerCase()] ?? Icons.Circle;
}

// ---------------------------------------------------------------------------
// Status indicator
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: FlowStepStatus }) {
  return (
    <span
      className={cn(
        "inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full",
        status === "completed" && "bg-success",
        status === "running" && "bg-status-selected shadow-[0_0_4px_var(--color-status-selected)] animate-pulse",
        status === "failed" && "bg-danger",
        status === "waiting" && "bg-warning",
        status === "pending" && "bg-muted",
      )}
      aria-hidden="true"
    />
  );
}

// ---------------------------------------------------------------------------
// Shared props
// ---------------------------------------------------------------------------

export interface FlowNodeProps {
  step: FlowStep;
  position: NodePosition;
  onHover?: (step: FlowStep) => void;
  onLeave?: () => void;
  onClick?: (step: FlowStep) => void;
  onApprove?: (stepId: string) => void;
  onReject?: (stepId: string) => void;
}

// ---------------------------------------------------------------------------
// Shared pill node — base for all node types
// ---------------------------------------------------------------------------

function PillNode({
  step,
  position,
  onHover,
  onLeave,
  onClick,
  borderStyle = "solid",
  highlight,
  children,
}: FlowNodeProps & {
  borderStyle?: "solid" | "dashed";
  highlight?: string;
  children?: React.ReactNode;
}) {
  const Icon = resolveIcon(step.icon);

  return (
    <div
      className={cn(
        "absolute flex items-center gap-1.5 rounded-full",
        "border border-glass-stroke",
        "bg-glass-fill/80 backdrop-blur-sm",
        "px-3 cursor-pointer transition-all",
        "hover:bg-glass-fill-heavy/40",
        borderStyle === "dashed" && "border-dashed",
        step.status === "completed" && "border-success/40",
        highlight,
      )}
      style={{
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
      }}
      data-testid={`flow-node-${step.id}`}
      onMouseEnter={() => onHover?.(step)}
      onMouseLeave={() => onLeave?.()}
      onClick={() => onClick?.(step)}
    >
      <Icon className="h-3 w-3 flex-shrink-0 text-muted" />
      <span className="flex-1 truncate text-canvas-base font-medium text-secondary">
        {children ?? step.title}
      </span>
      <StatusDot status={step.status} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. TriggerNode — dashed pill
// ---------------------------------------------------------------------------

function TriggerNode(props: FlowNodeProps) {
  const description =
    props.step.triggerConfig?.type === "cron"
      ? props.step.triggerConfig.schedule ?? "Scheduled"
      : props.step.triggerConfig?.type === "event"
        ? props.step.triggerConfig.eventDescription ?? "On event"
        : "Manual";

  return (
    <PillNode {...props} borderStyle="dashed">
      {description}
    </PillNode>
  );
}

// ---------------------------------------------------------------------------
// 2. AgentTaskNode — solid pill
// ---------------------------------------------------------------------------

function AgentTaskNode(props: FlowNodeProps) {
  return <PillNode {...props} />;
}

// ---------------------------------------------------------------------------
// 3. ToolCallNode — solid pill
// ---------------------------------------------------------------------------

function ToolCallNode(props: FlowNodeProps) {
  return <PillNode {...props} />;
}

// ---------------------------------------------------------------------------
// 4. ApprovalGateNode — dashed pill with amber highlight when waiting
// ---------------------------------------------------------------------------

function ApprovalGateNode(props: FlowNodeProps) {
  return (
    <PillNode
      {...props}
      borderStyle="dashed"
      highlight={
        props.step.status === "waiting"
          ? "border-warning/50 bg-warning-muted"
          : undefined
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 5. ConditionalNode — smaller badge-like pill with dashed border
// ---------------------------------------------------------------------------

function ConditionalNode(props: FlowNodeProps) {
  return <PillNode {...props} borderStyle="dashed" />;
}

// ---------------------------------------------------------------------------
// FlowNode dispatcher
// ---------------------------------------------------------------------------

const NODE_COMPONENTS: Record<
  FlowStep["type"],
  React.ComponentType<FlowNodeProps>
> = {
  trigger: TriggerNode,
  agent_task: AgentTaskNode,
  tool_call: ToolCallNode,
  approval_gate: ApprovalGateNode,
  conditional: ConditionalNode,
};

/** Routes to the correct node component based on step.type. */
export function FlowNode({ step, position, onHover, onLeave, onClick, onApprove, onReject }: FlowNodeProps) {
  const Component = NODE_COMPONENTS[step.type];
  return (
    <Component
      step={step}
      position={position}
      onHover={onHover}
      onLeave={onLeave}
      onClick={onClick}
      onApprove={onApprove}
      onReject={onReject}
    />
  );
}

export {
  TriggerNode,
  AgentTaskNode,
  ToolCallNode,
  ApprovalGateNode,
  ConditionalNode,
  StatusDot,
  resolveIcon,
};
